/**
 * MaterialSymbolsPreconnect
 *
 * Injects preconnect resource hints for Google Fonts CDN into <head>.
 * Place this component AS EARLY AS POSSIBLE in the app tree, ideally
 * directly inside <head> or root layout.
 *
 * WHY THIS MATTERS:
 * If @import url() is inside a CSS file, browser must:
 * 1. Parse HTML -> download JS bundle -> execute CSS -> hit @import -> start Google Fonts connection
 * Preconnect hints allow browser to establish TCP handshake + TLS connection early at step 1,
 * saving 100-500ms connection latency depending on network.
 *
 * USAGE:
 * ```tsx
 * // app/layout.tsx (Next.js) or index.html equivalent
 * import { MaterialSymbolsPreconnect } from '@bug-on/m3-expressive';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <head>
 *         <MaterialSymbolsPreconnect />
 *       </head>
 *       <body>{children}</body>
 *     </html>
 *   );
 * }
 * ```
 *
 * NOTE: Use this component only with CDN mode.
 * Self-hosted fonts do not require preconnecting to external origins.
 */
export interface MaterialSymbolsPreconnectProps {
	/**
	 * Array of Material Symbols font variants to load.
	 * Include only variants used by application to minimize bandwidth.
	 * @default ["outlined"]
	 */
	variants?: Array<"outlined" | "rounded" | "sharp">;
}

export function MaterialSymbolsPreconnect({
	variants = ["outlined"],
}: MaterialSymbolsPreconnectProps) {
	return (
		<>
			{/* Preconnect for CSS stylesheet */}
			<link rel="preconnect" href="https://fonts.googleapis.com" />
			{/* Preconnect for font files — crossorigin required for CORS request */}
			<link
				rel="preconnect"
				href="https://fonts.gstatic.com"
				crossOrigin="anonymous"
			/>
			{/* Load selected font variants.
			    Uses display=swap for performance optimization (Lighthouse/Next.js recommendation).
			    Note: May cause brief ligature flicker during initial load. */}

			{variants.includes("outlined") && (
				<link
					href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap"
					rel="stylesheet"
					precedence="default"
				/>
			)}
			{variants.includes("rounded") && (
				<link
					href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap"
					rel="stylesheet"
					precedence="default"
				/>
			)}
			{variants.includes("sharp") && (
				<link
					href="https://fonts.googleapis.com/css2?family=Material+Symbols+Sharp:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap"
					rel="stylesheet"
					precedence="default"
				/>
			)}
		</>
	);
}
